/**
 * RevenueFirstOrderValueRow — the THIRD money population on Revenue Calculation (card 2458).
 *
 * Presentational and pure: it renders the `firstOrderValue` block of
 * `GET /admin/revenue-calculation/summary` and derives NOTHING. Every label, basis and term
 * note is the API's own string, so the wording lives once — in the server's constants — and
 * this file cannot drift from the other five surfaces that publish the same metric.
 *
 * 🔑 WHY A THIRD ROW RATHER THAN A RECONCILIATION.
 *   This page already renders two money rows that measure DIFFERENT populations, and this is
 *   a third one again, each labelled with its own predicate:
 *     · RevenueStoredEconomicsRow — canonical reporting population, reporting exclusion applied.
 *     · RevenueKpiRow             — catalog/stored `orders.*` columns, status-filtered.
 *     · this row                  — purchase day + initial fulfillment, renewals EXCLUDED.
 *   Measured live 2026-08-27 on the default window: 27 orders read, of which 21 are initial
 *   and 6 are renewals that are read and then excluded. ⛔ These three are NOT supposed to
 *   agree, and nothing here may reconcile them — card 2460 owns agreement WITHIN this metric.
 *
 * 🔑 THE MONEY ARRIVES AS BARE INTEGER CENTS, NOT AS A METRIC OBJECT.
 *   The payload publishes `totalsCents: { grossRevenue: 710541, … }` — plain numbers — while
 *   the shared formatter grades `{ value, dataQuality }`. `toFirstOrderValueMetric` is the
 *   ONLY adapter between the two, so `formatMetricValue` stays the single formatter on this
 *   page. A figure the API did not publish becomes `value: null`, which the shared rule
 *   renders as missing — ⛔ never $0.00, which would print a 100% margin on an unmeasured
 *   figure. See utils/revenueMetrics.js for why that guard is written by TYPE.
 */

import {
  isBlockedMetric,
  formatMetricValue,
  describeMetricBasis
} from '../../utils/revenueMetrics';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/**
 * The six published figures in render order. Pure data — the row maps this list.
 * `money` is read from `totalsCents` (integer cents, what `formatPrice` takes);
 * `percent` is read from `totals` because a margin is a RATIO and has no cents form.
 */
export const FIRST_ORDER_VALUE_DEFS = Object.freeze([
  { key: 'grossRevenue', label: 'Gross revenue', kind: 'money' },
  { key: 'reserveWithheld', label: 'Reserve withheld', kind: 'money' },
  { key: 'netRevenue', label: 'Net revenue', kind: 'money' },
  { key: 'totalExpense', label: 'Total expense', kind: 'money' },
  { key: 'profit', label: 'Profit', kind: 'money' },
  { key: 'margin', label: 'Margin', kind: 'percent' }
]);

/**
 * PURE. One published figure → the metric shape the shared formatter grades.
 *
 * A key the API did not publish yields `value: null` and `dataQuality: 'missing'`, so the
 * shared rule renders it as missing rather than inventing a zero. `basis` is carried through
 * so the tile's tooltip explains the figure with the API's own sentence.
 */
export const toFirstOrderValueMetric = (firstOrderValue, def) => {
  const raw = def.kind === 'percent'
    ? firstOrderValue?.totals?.[def.key]
    : firstOrderValue?.totalsCents?.[def.key];
  const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
  return {
    value,
    dataQuality: value === null ? 'missing' : 'verified',
    basis: firstOrderValue?.basis ?? '',
    source: 'order_first_order_value'
  };
};

/** Pure. One figure. A figure with no published value is styled AND labelled as missing. */
const FirstOrderValueTile = ({ def, metric }) => {
  const blocked = isBlockedMetric(metric);
  return (
    <div
      className={styles.kpiTile}
      data-testid={`first-order-value-${def.key}`}
      data-blocked={blocked ? 'true' : 'false'}
      title={describeMetricBasis(metric)}
    >
      <p className={styles.kpiLabel}>{def.label}</p>
      <p className={blocked ? styles.kpiValueBlocked : styles.kpiValue}>
        {formatMetricValue(metric, def.kind)}
      </p>
    </div>
  );
};

/**
 * The First Order Value row. `firstOrderValue` is the summary payload's block of that name.
 *
 * `renewalsExcluded` is REPORTED rather than dropped silently: a renewal is read by the
 * aggregation and then excluded, and a reader has to be able to see how many were removed
 * rather than infer it from a total that does not match the other two rows.
 */
const RevenueFirstOrderValueRow = ({ firstOrderValue }) => {
  if (!firstOrderValue) return null;

  const initialOrderCount = Number(firstOrderValue.initialOrderCount ?? 0);
  const renewalsExcluded = Number(firstOrderValue.renewalsExcluded ?? 0);
  const withoutRow = Number(firstOrderValue.ordersWithoutFirstOrderValue ?? 0);
  const orderCount = Number(firstOrderValue.orderCount ?? 0);

  return (
    <div className={styles.populationBlock} data-testid="first-order-value-row">
      <p className={styles.sectionLabel} data-testid="first-order-value-label">
        {firstOrderValue.label} · a THIRD population · version {firstOrderValue.observedFirstOrderValueVersionsText}
      </p>
      <p className={styles.sectionNote} data-testid="first-order-value-basis">
        {firstOrderValue.basis}
      </p>
      <p className={styles.sectionNote} data-testid="first-order-value-term-note">
        {firstOrderValue.termNote}
      </p>

      <p className={styles.scopeLine} data-testid="first-order-value-scope">
        Scope <code>{firstOrderValue.scope}</code> · {initialOrderCount} initial order
        {initialOrderCount === 1 ? '' : 's'} of {orderCount} read in the window.
      </p>
      <p className={styles.scopeLine} data-testid="first-order-value-renewals-excluded">
        {renewalsExcluded} renewal{renewalsExcluded === 1 ? '' : 's'} read and then excluded. A
        renewal is not an initial order, so it is reported here rather than folded into the
        figures below.
      </p>

      {withoutRow > 0 && (
        <p className={styles.notice} data-testid="first-order-value-gap">
          {withoutRow} initial order{withoutRow === 1 ? '' : 's'} in this window carry no stored
          row and are counted separately — never as $0.00, which would understate every figure
          below.
        </p>
      )}

      <div className={styles.kpiGrid} data-testid="first-order-value-grid">
        {FIRST_ORDER_VALUE_DEFS.map((def) => (
          <FirstOrderValueTile
            key={def.key}
            def={def}
            metric={toFirstOrderValueMetric(firstOrderValue, def)}
          />
        ))}
      </div>
    </div>
  );
};

export default RevenueFirstOrderValueRow;
